import React, { useState, useRef, useEffect } from 'react';
import { 
  User, 
  Calendar, 
  Users,
  Package,
  Settings,
  Activity,
  FileText,
  Mail,
  Bell,
  LogOut,
  LayoutDashboard,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../../../state/auth/useAuth';
import Logo from '../../../components/Logo';

const TopNavBar = ({ user, activeTab, onTabChange }) => {
  const { logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Admin dashboard tabs
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'master-data', label: 'Master Data', icon: Activity },
    { id: 'patients', label: 'Users', icon: Users },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'finance', label: 'Finance & Billing', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileRef]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
    }
  };

  return (
    <div className="bg-white border-b-2 border-gray-200 shadow-sm fixed top-0 left-0 right-0 z-50" style={{ fontFamily: 'Poppins' }}>
      <div className="flex items-center justify-between px-6 py-4">
        {/* Logo Section */}
        <div className="flex items-center min-w-[160px]">
          <Logo size="sm" showText={true} />
        </div>

        {/* Navigation Tabs - Centered */}
        <nav className="flex items-center bg-gray-100 rounded-full p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  px-5 py-2.5 text-lg font-semibold transition-all duration-200 rounded-full whitespace-nowrap
                  ${isActive 
                    ? 'bg-[#2D869C] text-white shadow-sm' 
                    : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Right Side - Icons and User Profile */}
        <div className="flex items-center space-x-3 min-w-[160px] justify-end">
          {/* Mail Icon */}
          <button className="w-11 h-11 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
            <Mail size={20} className="text-gray-600" />
          </button>

          {/* Notifications */}
          <button className="w-11 h-11 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors relative">
            <Bell size={20} className="text-gray-600" />
          </button>

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center focus:outline-none"
            >
              <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#2D869C] shadow-sm">
                {user?.avatar || user?.profilePicture?.url ? (
                  <img 
                    src={user?.avatar || user?.profilePicture?.url} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full bg-[#9DC9D4]/30 flex items-center justify-center text-[#2D869C]">
                    <User size={20} />
                  </div>
                )}
              </div>
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                  <p className="text-md font-bold text-gray-900 truncate">{user?.name || 'Admin'}</p>
                  <p className="text-sm text-gray-500 truncate font-medium mt-0.5">{user?.email || 'No email'}</p>
                  {user?.role && (
                    <div className="mt-2 inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">
                      {user.role}
                    </div>
                  )}
                </div>

                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-5 py-3 text-md text-red-600 hover:bg-red-50 flex items-center transition-colors font-semibold border-t border-gray-200 pt-3"
                  >
                    <LogOut size={18} className="mr-3" />
                    Sign out
                  </button>
                </div>

            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopNavBar;
